/* eslint-disable no-unused-vars */
import { Band, VisitStatusWithName, TreatmentScheduleWithName } from '../../../../enums';
import { stringToArray, removeSpaces } from '../../../../utils/helpers';

export class AdvanceFiltersHandler {
  setNext(handler) {
    this.next = handler;
    return handler;
  }

  setRequest(request) {
    this.request = request;
  }

  handle(treatmentInstances, assignments, cancelledTreatmentInstances, clinicianTreatmentInstances) {
    if (this.next) {
      return this.next.handle(
        treatmentInstances,
        assignments,
        cancelledTreatmentInstances,
        clinicianTreatmentInstances,
      );
    }
    return { treatmentInstances, assignments, cancelledTreatmentInstances, clinicianTreatmentInstances };
  }
}

export class VisitTypeFilterHandler extends AdvanceFiltersHandler {
  handle(treatmentInstances, assignments, cancelledTreatmentInstances, clinicianTreatmentInstances) {
    treatmentInstances = treatmentInstances.filter(ti => this.request.includes(ti.visitType));
    assignments = assignments.filter(a => this.request.includes(a.visitType));
    cancelledTreatmentInstances = cancelledTreatmentInstances.filter(ct => this.request.includes(ct.visitType));
    clinicianTreatmentInstances = clinicianTreatmentInstances.map(data => ({
      ...data,
      items: data.items.filter(ti => this.request.includes(ti.visitType)),
    }));
    return super.handle(treatmentInstances, assignments, cancelledTreatmentInstances, clinicianTreatmentInstances);
  }
}

export class VisitStatuseFilterHandler extends AdvanceFiltersHandler {
  handle(treatmentInstances, assignments, cancelledTreatmentInstances, clinicianTreatmentInstances) {
    const isRescheduledFilterOn = this.request.includes(
      VisitStatusWithName.find(v => v.key === 'rescheduledVisits').id,
    );
    const isUnassignFilterOn = this.request.includes(VisitStatusWithName.find(v => v.key === 'unassignedVisits').id);
    const isCancelledVisitsFilterOn = this.request.includes(
      VisitStatusWithName.find(v => v.key === 'cancelledVisits').id,
    );
    if (isCancelledVisitsFilterOn && !isUnassignFilterOn) {
      treatmentInstances = [];
      clinicianTreatmentInstances = clinicianTreatmentInstances.map(data => ({
        ...data,
        items: [],
      }));
    }
    if (!isCancelledVisitsFilterOn) {
      cancelledTreatmentInstances = [];
    }
    if (isRescheduledFilterOn) {
      treatmentInstances = treatmentInstances.filter(ti => ti.hasMovedFrom);
      assignments = assignments.filter(a => a.hasMovedFrom);
      cancelledTreatmentInstances = cancelledTreatmentInstances.filter(ct => ct.hasMovedFrom);
      clinicianTreatmentInstances = clinicianTreatmentInstances.map(data => ({
        ...data,
        items: data.items.filter(ct => ct.hasMovedFrom),
      }));
    }
    return super.handle(treatmentInstances, assignments, cancelledTreatmentInstances, clinicianTreatmentInstances);
  }
}

export class NhsFilterHandler extends AdvanceFiltersHandler {
  handle(treatmentInstances, assignments, cancelledTreatmentInstances, clinicianTreatmentInstances) {
    const nhsNumber = removeSpaces(this.request);
    treatmentInstances = treatmentInstances.filter(ti => removeSpaces(ti.nhsNumber) === nhsNumber);
    assignments = assignments.filter(a => removeSpaces(a.nhsNumber) === nhsNumber);
    cancelledTreatmentInstances = cancelledTreatmentInstances.filter(ct => removeSpaces(ct.nhsNumber) === nhsNumber);
    clinicianTreatmentInstances = clinicianTreatmentInstances.map(data => ({
      ...data,
      items: data.items.filter(ct => removeSpaces(ct.nhsNumber) === nhsNumber),
    }));
    return super.handle(treatmentInstances, assignments, cancelledTreatmentInstances, clinicianTreatmentInstances);
  }
}

export class ActivityFilterHandler extends AdvanceFiltersHandler {
  handle(treatmentInstances, assignments, cancelledTreatmentInstances, clinicianTreatmentInstances) {
    treatmentInstances = treatmentInstances.filter(ti =>
      stringToArray(ti.activities).some(ac => this.request.includes(ac)),
    );
    assignments = assignments.filter(a => stringToArray(a.primaryLabel).some(ac => this.request.includes(ac)));
    cancelledTreatmentInstances = cancelledTreatmentInstances.filter(ct =>
      stringToArray(ct.activities).some(ac => this.request.includes(ac)),
    );
    clinicianTreatmentInstances = clinicianTreatmentInstances.map(data => ({
      ...data,
      items: data.items.filter(ct => stringToArray(ct.activities).some(ac => this.request.includes(ac))),
    }));
    return super.handle(treatmentInstances, assignments, cancelledTreatmentInstances, clinicianTreatmentInstances);
  }
}

export class MultipleCliniciansFilterHandler extends AdvanceFiltersHandler {
  handle(treatmentInstances, assignments, cancelledTreatmentInstances, clinicianTreatmentInstances) {
    const shouldHaveMultipleClinicians = this.request[0] === 0;

    const filterByClinicians = instances => {
      return instances.filter(item =>
        shouldHaveMultipleClinicians
          ? item.numberOfClinicians > 1 || item.additionalClinician > 0
          : item.numberOfClinicians === 1 && (!item.additionalClinician || item.additionalClinician === 0),
      );
    };

    treatmentInstances = filterByClinicians(treatmentInstances);
    assignments = filterByClinicians(assignments);
    cancelledTreatmentInstances = filterByClinicians(cancelledTreatmentInstances);
    clinicianTreatmentInstances = clinicianTreatmentInstances.map(data => ({
      ...data,
      items: filterByClinicians(data.items),
    }));

    return super.handle(treatmentInstances, assignments, cancelledTreatmentInstances, clinicianTreatmentInstances);
  }
}

export class PostcodeFilterHandler extends AdvanceFiltersHandler {
  handle(treatmentInstances, assignments, cancelledTreatmentInstances, clinicianTreatmentInstances) {
    const postcode = removeSpaces(this.request).toUpperCase();
    treatmentInstances = treatmentInstances.filter(ti => removeSpaces(ti.postcode.toUpperCase()).includes(postcode));
    assignments = assignments.filter(a => removeSpaces(a.postcode.toUpperCase()).includes(postcode));
    cancelledTreatmentInstances = cancelledTreatmentInstances.filter(ct =>
      removeSpaces(ct.postcode.toUpperCase()).includes(postcode),
    );
    clinicianTreatmentInstances = clinicianTreatmentInstances.map(data => ({
      ...data,
      items: data.items.filter(ct => removeSpaces(ct.postcode.toUpperCase()).includes(postcode)),
    }));
    return super.handle(treatmentInstances, assignments, cancelledTreatmentInstances, clinicianTreatmentInstances);
  }
}

export class RagFilterHandler extends AdvanceFiltersHandler {
  handle(treatmentInstances, assignments, cancelledTreatmentInstances, clinicianTreatmentInstances) {
    treatmentInstances = treatmentInstances.filter(ti => this.request.includes(ti.ragStatus.value));
    assignments = assignments.filter(a => this.request.includes(a.ragStatus.value));
    cancelledTreatmentInstances = cancelledTreatmentInstances.filter(ct => this.request.includes(ct.ragStatus.value));
    clinicianTreatmentInstances = clinicianTreatmentInstances.map(data => ({
      ...data,
      items: data.items.filter(ct => this.request.includes(ct.ragStatus.value)),
    }));
    return super.handle(treatmentInstances, assignments, cancelledTreatmentInstances, clinicianTreatmentInstances);
  }
}

export class BandFilterHandler extends AdvanceFiltersHandler {
  handle(treatmentInstances, assignments, cancelledTreatmentInstances, clinicianTreatmentInstances) {
    const bandKey = this.request.map(id => Band.find(b => b.id === id).key);

    treatmentInstances = treatmentInstances.filter(ti => bandKey.includes(ti.band));
    assignments = assignments.filter(a => this.request.includes(a.band));
    cancelledTreatmentInstances = cancelledTreatmentInstances.filter(ct => bandKey.includes(ct.band));
    clinicianTreatmentInstances = clinicianTreatmentInstances.map(data => ({
      ...data,
      items: data.items.filter(ct => bandKey.includes(ct.band)),
    }));
    return super.handle(treatmentInstances, assignments, cancelledTreatmentInstances, clinicianTreatmentInstances);
  }
}

export class TreatmentScheduleFilterHandler extends AdvanceFiltersHandler {
  handle(treatmentInstances, assignments, cancelledTreatmentInstances, clinicianTreatmentInstances) {
    const treatmentScheduleKeys = this.request.map(id => TreatmentScheduleWithName.find(ts => ts.id === id).key);

    treatmentInstances = treatmentInstances.filter(ti => treatmentScheduleKeys.includes(ti.treatmentTimeType));
    assignments = assignments.filter(a => treatmentScheduleKeys.includes(a.treatmentTimeType));
    cancelledTreatmentInstances = cancelledTreatmentInstances.filter(ct =>
      treatmentScheduleKeys.includes(ct.treatmentTimeType),
    );
    clinicianTreatmentInstances = clinicianTreatmentInstances.map(data => ({
      ...data,
      items: data.items.filter(ct => treatmentScheduleKeys.includes(ct.treatmentTimeType)),
    }));
    return super.handle(treatmentInstances, assignments, cancelledTreatmentInstances, clinicianTreatmentInstances);
  }
}
